// src/utils/apiClient.js

import { setServerError } from '../../redux/actions/appActions';
import store from '../../redux/store/reduxStore';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
  }

  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      headers = {},
      payload,
      ignoreServerError = false,
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    const requestOptions = {
      method,
      headers: { ...this.defaultHeaders, ...headers },
    };

    if (method !== 'GET' && payload) {
      requestOptions.body = JSON.stringify(payload);
    }

    try {
      const response = await fetch(url, requestOptions);
      return await this.handleResponse(response, ignoreServerError);
    } catch (error) {
      return this.handleNetworkError(error, ignoreServerError);
    }
  }

  async handleResponse(response, ignoreServerError) {
    if (response.status === 204) {
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    }

    if (response.status >= 400 && response.status < 500) {
      throw new ApiError('Client error', response.status, data);
    }

    if (response.status >= 500) {
      if (!ignoreServerError) {
        store.dispatch(setServerError(true));
      }
      throw new ApiError('Server error', response.status, data);
    }
  }

  handleNetworkError(error, ignoreServerError) {
    console.error('Network error:', error);
    if (!ignoreServerError) {
      store.dispatch(setServerError(true));
    }
    throw new ApiError('Network error', null, { error: "An error occurred! Please refresh the page." });
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, payload, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', payload });
  }

  async put(endpoint, payload, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', payload });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async patch(endpoint, payload, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', payload });
  }
}